// src/components/property/video-settings/DefaultsTab.tsx
/**
 * DefaultsTab Component
 * Tab for setting default templates per category
 *
 * Feature: Video Settings
 */

import { useMemo } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import type { IDefaultsTabProps, TemplateCategory, TemplateId } from '@/@types/videoSettings';

/**
 * Display template type that unifies system and user templates
 */
interface IDisplayTemplate {
  _id: TemplateId;
  name: string;
  isUserTemplate?: boolean;
}

const CATEGORIES: { key: TemplateCategory; label: string; description: string }[] = [
  { key: 'building', label: 'Building', description: 'Building facade and front views' },
  {
    key: 'outdoor',
    label: 'Outdoor',
    description: 'Backyard, pool, garden, parking, garage, balcony',
  },
  { key: 'interior', label: 'Interior', description: 'Living room, kitchen, bedroom, bathroom' },
  { key: 'other', label: 'Other', description: 'Floor plans and other images' },
];

const CATEGORY_IMAGE_TYPES: Record<TemplateCategory, string[]> = {
  building: ['building'],
  outdoor: ['backyard', 'pool', 'garden', 'parking', 'garage', 'balcony'],
  interior: ['living_room', 'kitchen', 'bedroom', 'bathroom'],
  other: ['plan', 'other'],
};

const NONE_VALUE = '__none__';

function DefaultsTab({
  preferences,
  systemTemplates,
  userTemplates,
  onSetDefault,
  isLoading,
}: IDefaultsTabProps) {
  // Get templates available for a category (non-excluded system + user templates)
  const getTemplatesForCategory = useMemo(() => {
    return (category: TemplateCategory): IDisplayTemplate[] => {
      const imageTypes = CATEGORY_IMAGE_TYPES[category];
      const excludedIds = preferences?.excludedTemplateIds ?? [];

      // Filter system templates and map to display format
      const availableSystem: IDisplayTemplate[] = systemTemplates
        .filter(t => {
          if (excludedIds.includes(t._id)) return false;
          return t.imageTypes.some(it => imageTypes.includes(it));
        })
        .map(t => ({
          _id: t._id,
          name: t.name,
          isUserTemplate: false,
        }));

      // Filter user templates by category and map to display format
      const availableUser: IDisplayTemplate[] = userTemplates
        .filter(t => t.categories.includes(category))
        .map(t => ({
          _id: t._id,
          name: t.name,
          isUserTemplate: true,
        }));

      return [...availableUser, ...availableSystem];
    };
  }, [systemTemplates, userTemplates, preferences?.excludedTemplateIds]);

  const handleChange = (category: TemplateCategory, value: string) => {
    if (value === NONE_VALUE) {
      onSetDefault(category, null);
    } else {
      onSetDefault(category, value as TemplateId);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        {CATEGORIES.map(cat => (
          <div key={cat.key} className="space-y-2">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-10 w-full" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-sm text-gray-600 dark:text-gray-400 mb-4">
        Set default templates for each scene category. When generating videos, these templates will
        be used automatically unless you choose a different one.
      </div>

      {CATEGORIES.map(cat => {
        const templates = getTemplatesForCategory(cat.key);
        const currentValue = preferences?.defaultTemplates[cat.key] ?? NONE_VALUE;

        return (
          <div key={cat.key} className="space-y-2">
            <div>
              <Label className="text-sm font-medium">{cat.label}</Label>
              <p className="text-xs text-gray-500 dark:text-gray-400">{cat.description}</p>
            </div>
            <Select value={currentValue} onValueChange={v => handleChange(cat.key, v)}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select default template..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE_VALUE}>
                  <span className="flex items-center gap-2">
                    <span>🎲</span>
                    <span>None (Random)</span>
                  </span>
                </SelectItem>
                {templates.map(template => (
                  <SelectItem key={template._id} value={template._id}>
                    <span className="flex items-center gap-2">
                      {template.isUserTemplate && (
                        <span className="text-xs bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 px-1.5 py-0.5 rounded">
                          Custom
                        </span>
                      )}
                      <span>{template.name}</span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );
      })}
    </div>
  );
}

export { DefaultsTab };
